<template>
    <div class="flex gap-2 px-2 py-2 w-full items-center relative">
      <transition name="hold-hint">
        <div v-if="showHoldHint" class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-gray-800 text-white text-xs px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap pointer-events-none z-10">
          {{ $root.getLangText({'ru': 'Удерживайте для записи аудио', 'en': 'Hold to record audio'}) }}
        </div>
      </transition>
      <template v-if="!hasRecording">
        <div class="flex items-center gap-3">
                <a-upload
                    name="file"
                    :multiple="false"
                    @change="handleChangeFile"
                    :show-upload-list="false"
                    :action="actionUploadUrl"
                >
                <button class="text-gray-500 hover:text-black mt-1" :style="{color: $root.hotel.color}">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7 md:w-8 md:h-8 fill-current" viewBox="0 0 512 512"><path d="M216.08 192v143.85a40.08 40.08 0 0080.15 0l.13-188.55a67.94 67.94 0 10-135.87 0v189.82a95.51 95.51 0 10191 0V159.74" fill="none" stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="32"/></svg>
                </button>
            </a-upload>
        </div>
        <div class="bg-gray-100 rounded-2xl p-1 w-full flex gap-x-4 items-end relative">
            <textarea
                v-if="!isRecording"
                v-model="s_content"
                rows="1"
                id="chat-input-message"
                ref="myTextarea"
                :disabled="disabled"
                @keydown.enter.exact.prevent
                @keyup.enter="handleEnterKey"
                @input="adjustTextareaHeight"
                class="bg-transparent w-full px-2 py-1 md:py-2 resize-none rounded-lg text-gray-900 text-sm focus:outline-none placeholder-gray-400 disabled:opacity-60 disabled:cursor-not-allowed" :placeholder="placeHolder">
            </textarea>
            <div v-else class="flex items-center gap-2 w-full px-2 py-2 text-sm text-gray-700">
                <span class="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0"></span>
                <span class="tabular-nums font-medium shrink-0">{{ recordingTimeText }}</span>
                <span v-if="!isLocked" class="truncate transition-colors" :class="willCancel ? 'text-red-500 font-medium' : 'text-gray-400'">
                    <template v-if="willCancel">{{ $root.getLangText({'ru': 'Отпустите для отмены', 'en': 'Release to cancel'}) }}</template>
                    <template v-else>{{ $root.getLangText({'ru': '‹ смахните для отмены', 'en': '‹ slide to cancel · ⤒ up to lock'}) }}</template>
                </span>
                <button v-else @click="cancelRecording" class="ml-auto shrink-0 font-medium text-red-500 hover:text-red-600 focus:outline-none">
                    {{ $root.getLangText({'ru': 'Отмена', 'en': 'Cancel'}) }}
                </button>
            </div>
        </div>
        <div v-if="isRecording && isLocked">
            <button @click="stopAndSendRecording" class="relative flex items-center justify-center w-10 h-10 rounded-full focus:outline-none" :title="$root.getLangText({'ru': 'Остановить', 'en': 'Stop'})">
                <span class="voice-ripple" :style="{backgroundColor: $root.hotel.color}"></span>
                <span class="voice-ripple voice-ripple--delayed" :style="{backgroundColor: $root.hotel.color}"></span>
                <span class="voice-record-dot" :style="{backgroundColor: $root.hotel.color}"></span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#fff" class="relative w-4 h-4"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
            </button>
        </div>
        <div v-else-if="voiceMessageActive && (!s_content || s_content.length === 0)" class="relative">
            <!-- slide-up-to-lock hint -->
            <div v-if="isRecording && !isLocked && !willCancel" class="voice-lock-hint absolute left-1/2 -translate-x-1/2 -top-14 flex flex-col items-center gap-1 text-gray-400 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z"/></svg>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4 voice-lock-chevron"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5"/></svg>
            </div>
            <button
                @pointerdown="onPressStart"
                @pointermove="onPressMove"
                @pointerup="onPressEnd"
                @pointercancel="onPressCancel"
                @contextmenu.prevent
                class="relative flex items-center justify-center w-10 h-10 rounded-full focus:outline-none touch-none select-none transition-transform"
                :class="[isRecording ? (willCancel ? 'text-red-500 scale-110' : 'scale-110') : 'hover:text-blue-600']"
                :style="{color: (isRecording && !willCancel) ? '#fff' : $root.hotel.color}"
                :title="$root.getLangText({'ru': 'Удерживайте для записи', 'en': 'Hold to record'})"
            >
                <span v-if="isRecording && !willCancel" class="voice-ripple" :style="{backgroundColor: $root.hotel.color}"></span>
                <span v-if="isRecording && !willCancel" class="voice-ripple voice-ripple--delayed" :style="{backgroundColor: $root.hotel.color}"></span>
                <span v-if="isRecording && !willCancel" class="voice-record-dot" :style="{backgroundColor: $root.hotel.color}"></span>

                <svg v-if="s_loading" class="relative w-8 h-8 fill-current" viewBox="0 0 100 100" enable-background="new 0 0 0 0">
                    <path d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                        <animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="360 50 50" repeatCount="indefinite"/>
                    </path>
                </svg>
                <svg v-else-if="isRecording && willCancel" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="relative w-6 h-6 md:w-7 md:h-7"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="relative w-6 h-6 md:w-7 md:h-7"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"/></svg>
            </button>
        </div>
        <div v-else>
            <button @click="sendMessage" class="block w-full h-full rounded-full focus:outline-none hover:text-blue-600 text-sm" :style="{color: $root.hotel.color}">
                <svg v-if="s_loading" class="w-8 h-8 fill-current" viewBox="0 0 100 100" enable-background="new 0 0 0 0">
                    <path d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                        <animateTransform 
                            attributeName="transform" 
                            attributeType="XML" 
                            type="rotate"
                            dur="1s" 
                            from="0 50 50"
                            to="360 50 50" 
                            repeatCount="indefinite" 
                        />
                    </path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 md:w-7 md:h-7 fill-current"  viewBox="0 0 512 512"><path d="M16 464l480-208L16 48v160l320 48-320 48z"/></svg>
            </button>
        </div>
      </template>

      <!-- Review recorded voice before sending -->
      <template v-else>
        <div>
            <button @click="cancelReview" :disabled="s_loading" class="flex items-center justify-center w-10 h-10 rounded-full focus:outline-none text-gray-400 hover:text-red-500 disabled:opacity-50" :title="$root.getLangText({'ru': 'Отменить', 'en': 'Cancel'})">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 md:w-7 md:h-7"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/></svg>
            </button>
        </div>
        <div class="bg-gray-100 rounded-2xl px-3 py-1 w-full flex items-center">
            <voice-message
                :src="pendingAudioUrl"
                :duration="pendingDuration"
                :sent="false"
                :color="$root.hotel.color"
            ></voice-message>
        </div>
        <div>
            <button @click="confirmSend" :disabled="s_loading" class="flex items-center justify-center w-10 h-10 rounded-full focus:outline-none text-sm" :style="{color: $root.hotel.color}" :title="$root.getLangText({'ru': 'Отправить', 'en': 'Send'})">
                <svg v-if="s_loading" class="w-8 h-8 fill-current" viewBox="0 0 100 100" enable-background="new 0 0 0 0">
                    <path d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                        <animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="360 50 50" repeatCount="indefinite"/>
                    </path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 md:w-7 md:h-7 fill-current" viewBox="0 0 512 512"><path d="M16 464l480-208L16 48v160l320 48-320 48z"/></svg>
            </button>
        </div>
      </template>
    </div>
</template>

<script>
import Loading from '../../components/article/ArticleLoading.vue';
import VoiceMessage from './VoiceMessage.vue';

export default {
    props: ['chat', 'content', 'loading', 'isActiveChatGpt', 'disabled', 'activeVoiceMessage'],

    components: {Loading, VoiceMessage},

    computed: {
        s_content: {
            get () {
                return this.content
            },
            set (val) {
                this.$emit('update:content', val)
            }
        },

        s_loading: {
            get () {
                return this.loading
            },
            set (val) {
                this.$emit('update:loading', val)
            }
        },

        actionUploadUrl() {
            return process.env.VUE_APP_API_URL + 'public/chat/' + this.chat.uuid + '/files';
        },

        // Single switch for the whole voice-message feature. Set to false to
        // disable recording/sending voice notes (the mic button falls back to
        // the normal send button).
        voiceMessageActive() {
            return this.activeVoiceMessage;
        },

        placeHolder() {
            return this.$root.getLangText({'ru': 'Отправить сообщение...', 'en': "Type here..."});
        },

        recordingTimeText() {
            const minutes = Math.floor(this.recordingSeconds / 60);
            const seconds = this.recordingSeconds % 60;
            return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        }
    },

    data() {
        return {
            visibleEmoji: false,
            visibleDynamicParametrs: false,

            loading_file_upload: false,

            isRecording: false,
            mediaRecorder: null,
            mediaStream: null,
            audioChunks: [],
            recordingSeconds: 0,
            recordingTimer: null,
            recordingMimeType: '',
            cancelledRecording: false,

            isPressing: false,
            willCancel: false,
            isLocked: false,
            pressStartX: 0,
            pressStartY: 0,
            recordStartTs: 0,
            releaseRequested: false,

            hasRecording: false,
            pendingAudioBlob: null,
            pendingBlobType: '',
            pendingDuration: 0,
            pendingAudioUrl: '',

            showHoldHint: false,
            holdHintTimer: null,
        }
    },

    created() {
        if ('visualViewport' in window) {
                            const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

        const VIEWPORT_VS_CLIENT_HEIGHT_RATIO = 0.75;
        window.visualViewport.addEventListener('resize', function (event) {
            if (!document.querySelector('.vm--modal'))
                return;
            
            document.querySelector('.vm--modal').style.top = '0'; 
            document.querySelector('.vm--modal').style.height =  Math.round(vh) + 'px';        
            document.querySelector('.vm--modal').style.minHeight =  Math.round(vh) + 'px'; 
            if (
            (event.target.height * event.target.scale) / window.screen.height <
            VIEWPORT_VS_CLIENT_HEIGHT_RATIO
            ) {
                document.querySelector('.vm--modal').style.height =  Math.round(event.target.height) + 'px'; 
                document.querySelector('.vm--modal').style.minHeight =  Math.round(event.target.height) + 'px'; 
                document.querySelector('.vm--modal').style.top =  Math.round(vh - event.target.height) + 'px'; 
                
            } else {
                
            }
        });
        }
    },

    beforeDestroy() {
        if (this.recordingTimer) {
            clearInterval(this.recordingTimer);
            this.recordingTimer = null;
        }
        try {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.cancelledRecording = true;
                this.mediaRecorder.stop();
            }
        } catch (e) {
            console.error(e);
        }
        this.stopMediaStream();
        this.revokePendingUrl();
        if (this.holdHintTimer) {
            clearTimeout(this.holdHintTimer);
            this.holdHintTimer = null;
        }
    },

    methods: {
        addEmoji(emoji) {
            this.s_content += emoji.native;
            this.visibleEmoji = false;
        },

        sendMessage() {
            if (this.disabled) return;
            this.$emit('send-message');
        },

        onPressStart(event) {
            if (this.disabled) return;
            if (this.s_loading || this.isRecording) return;
            // ignore non-primary mouse buttons
            if (event.button !== undefined && event.button !== 0) return;

            if (event.cancelable) event.preventDefault();

            this.isPressing = true;
            this.willCancel = false;
            this.isLocked = false;
            this.releaseRequested = false;
            this.pressStartX = event.clientX;
            this.pressStartY = event.clientY;

            try {
                if (event.pointerId !== undefined) event.currentTarget.setPointerCapture(event.pointerId);
            } catch (e) { /* ignore */ }

            this.startRecording();
        },

        onPressMove(event) {
            if (!this.isPressing || !this.isRecording) return;

            // slide up past the threshold -> lock hands-free recording
            const dy = this.pressStartY - event.clientY;
            if (dy > 60) {
                this.lockRecording(event);
                return;
            }

            // slide left from the button to enter the cancel zone
            const dx = this.pressStartX - event.clientX;
            this.willCancel = dx > 70;
        },

        lockRecording(event) {
            this.isLocked = true;
            this.willCancel = false;
            this.isPressing = false;

            // end the press but keep recording; the held button is about to unmount
            try {
                if (event && event.pointerId !== undefined) event.currentTarget.releasePointerCapture(event.pointerId);
            } catch (e) { /* ignore */ }
        },

        onPressEnd(event) {
            if (!this.isPressing) return;
            this.isPressing = false;

            try {
                if (event && event.pointerId !== undefined) event.currentTarget.releasePointerCapture(event.pointerId);
            } catch (e) { /* ignore */ }

            // released before the recorder finished starting up
            if (!this.isRecording) {
                this.releaseRequested = true;
                return;
            }

            this.finishPress();
        },

        onPressCancel() {
            if (!this.isPressing && !this.isRecording) return;
            this.isPressing = false;
            this.willCancel = true;

            if (this.isRecording) {
                this.finishPress();
            } else {
                this.releaseRequested = true;
            }
        },

        finishPress() {
            const tooShort = (Date.now() - this.recordStartTs) < 600;
            if (this.willCancel || tooShort) {
                this.cancelRecording();
                if (tooShort && !this.willCancel) {
                    this.flashHoldHint();
                }
            } else {
                this.stopAndSendRecording();
            }
            this.willCancel = false;
        },

        flashHoldHint() {
            this.showHoldHint = true;
            if (this.holdHintTimer) clearTimeout(this.holdHintTimer);
            this.holdHintTimer = setTimeout(() => {
                this.showHoldHint = false;
                this.holdHintTimer = null;
            }, 2000);
        },

        pickRecordingMimeType() {
            const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
            if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
                return '';
            }
            return candidates.find(type => MediaRecorder.isTypeSupported(type)) || '';
        },

        async startRecording() {
            if (this.isRecording) {
                return;
            }

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
                alert(this.$root.getLangText({'ru': 'Запись голоса не поддерживается на этом устройстве.', 'en': 'Voice recording is not supported on this device.'}));
                return;
            }

            try {
                this.mediaStream = await navigator.mediaDevices.getUserMedia({audio: true});
            } catch (e) {
                alert(this.$root.getLangText({'ru': 'Нет доступа к микрофону.', 'en': 'Microphone access denied.'}));
                return;
            }

            this.audioChunks = [];
            this.cancelledRecording = false;
            this.recordingMimeType = this.pickRecordingMimeType();

            const options = this.recordingMimeType ? {mimeType: this.recordingMimeType} : {};
            this.mediaRecorder = new MediaRecorder(this.mediaStream, options);

            this.mediaRecorder.addEventListener('dataavailable', (event) => {
                if (event.data && event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            });

            this.mediaRecorder.addEventListener('stop', () => {
                this.handleRecordingStop();
            });

            this.mediaRecorder.start();
            this.recordStartTs = Date.now();

            // user lifted the finger before the mic was ready -> discard
            if (this.releaseRequested) {
                this.releaseRequested = false;
                this.cancelledRecording = true;
                this.stopRecorder();
                this.flashHoldHint();
                return;
            }

            this.isRecording = true;
            this.recordingSeconds = 0;
            this.recordingTimer = setInterval(() => {
                this.recordingSeconds += 1;
            }, 1000);
        },

        stopAndSendRecording() {
            if (!this.isRecording || !this.mediaRecorder) {
                return;
            }
            this.cancelledRecording = false;
            this.stopRecorder();
        },

        cancelRecording() {
            if (!this.isRecording || !this.mediaRecorder) {
                return;
            }
            this.cancelledRecording = true;
            this.stopRecorder();
        },

        stopRecorder() {
            if (this.recordingTimer) {
                clearInterval(this.recordingTimer);
                this.recordingTimer = null;
            }
            this.isRecording = false;
            this.isLocked = false;

            try {
                if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                    this.mediaRecorder.stop();
                }
            } catch (e) {
                console.error(e);
            }
        },

        handleRecordingStop() {
            this.stopMediaStream();

            const keep = !this.cancelledRecording && this.audioChunks.length > 0;
            const chunks = this.audioChunks;
            this.audioChunks = [];
            this.mediaRecorder = null;

            if (!keep) {
                return;
            }

            // Hold the recording so the user can review, then send or cancel.
            const blobType = this.recordingMimeType || 'audio/webm';
            this.pendingBlobType = blobType;
            this.pendingDuration = this.recordingSeconds;
            this.pendingAudioBlob = new Blob(chunks, {type: blobType});
            this.revokePendingUrl();
            this.pendingAudioUrl = URL.createObjectURL(this.pendingAudioBlob);
            this.hasRecording = true;
        },

        cancelReview() {
            if (this.s_loading) return;
            this.clearReview();
        },

        clearReview() {
            this.revokePendingUrl();
            this.hasRecording = false;
            this.pendingAudioBlob = null;
            this.pendingBlobType = '';
            this.pendingDuration = 0;
        },

        revokePendingUrl() {
            if (this.pendingAudioUrl) {
                try { URL.revokeObjectURL(this.pendingAudioUrl); } catch (e) { /* ignore */ }
                this.pendingAudioUrl = '';
            }
        },

        async confirmSend() {
            if (this.s_loading || !this.pendingAudioBlob) return;
            await this.uploadVoiceMessage(this.pendingAudioBlob, this.pendingBlobType, this.pendingDuration);
        },

        async uploadVoiceMessage(audioBlob, blobType, duration) {
            const extension = blobType.indexOf('mp4') > -1 ? 'm4a' : (blobType.indexOf('ogg') > -1 ? 'ogg' : 'webm');
            const fileName = `voice-message-${Date.now()}.${extension}`;

            const formData = new FormData();
            formData.append('file', audioBlob, fileName);

            this.s_loading = true;
            try {
                let response = await this.$http.post(this.actionUploadUrl, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                this.$emit('send-voice-message', response.data.data, duration);
                this.clearReview();
            } catch (e) {
                console.error(e);
                this.s_loading = false;
                alert(this.$root.getLangText({'ru': 'Не удалось отправить голосовое сообщение.', 'en': 'Failed to send voice message.'}));
            }
        },

        stopMediaStream() {
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => track.stop());
                this.mediaStream = null;
            }
        },

        handleChangeFile(info) {
            if (this.disabled) return;
            if (info.file.status == 'uploading') {
                this.s_loading = true;
            }

            if (info.file.status === 'done') {
                //this.loading_file_upload = false;
                this.$emit('send-file-message', info.file.response.data);
            } else if (info.file.status === 'error') {
                this.s_loading = false;
            }

            console.log('uploading');
        },        

        addDynamicParametrToContent(param) {
            this.s_content += '{{'+ param+ '}}';
            this.visibleDynamicParametrs = false;
        },

        adjustTextareaHeight() {
            const textarea = document.querySelector('#chat-input-message');
            textarea.style.height = 'auto'; // Reset the height to auto to recalculate properly
            textarea.style.height = `${textarea.scrollHeight}px`; // Set the height to the scroll height
        },         

        handleEnterKey(event) {
            if (this.disabled) return;
            if (!this.isMobileDevice()) {
                event.preventDefault(); // Prevent default Enter key behavior on desktop
                this.sendMessage();
                // Handle your custom logic here
            } else {
                this.s_content += "\n";
                this.adjustTextareaHeight();
            }
        },

        isMobileDevice() {
            const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
            return mobileRegex.test(navigator.userAgent);
        },
    }
}
</script>

<style scoped>
/* solid colored fill shown behind the icon while recording */
.voice-record-dot {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    opacity: 1;
    pointer-events: none;
}

/* expanding ripples emanating from the record button */
.voice-ripple {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    opacity: 0.4;
    pointer-events: none;
    animation: voiceRipple 1.6s ease-out infinite;
}

.voice-ripple--delayed {
    animation-delay: 0.8s;
}

@keyframes voiceRipple {
    0% {
        transform: scale(0.85);
        opacity: 0.45;
    }
    100% {
        transform: scale(2.6);
        opacity: 0;
    }
}

/* floating "slide up to lock" hint */
.voice-lock-hint {
    animation: voiceLockFloat 1.4s ease-in-out infinite;
}

.voice-lock-chevron {
    animation: voiceChevronBounce 1.2s ease-in-out infinite;
}

@keyframes voiceLockFloat {
    0%, 100% { opacity: 0.55; }
    50% { opacity: 1; }
}

@keyframes voiceChevronBounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
}

/* "Hold to record" toast */
.hold-hint-enter-active,
.hold-hint-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.hold-hint-enter,
.hold-hint-leave-to {
    opacity: 0;
    transform: translate(-50%, 6px);
}
</style>
